import { PATH_LINGUIST, homePathFor } from 'src/routes/paths';

// ---------------------------------------------------------------------------
// THE VENDOR PORTAL'S NAVIGATION (DESIGN.md §12, for an external portal).
//
// Every destination the old sidebar offered, by account type, and nothing it
// did not: the routes and who may reach them are the router's
// (`src/routes/index.js`, one AuthGuard branch per type). What changed is the
// shape — named groups once a type has more than three places to go, the
// glyph of the registry (§10), and the PATHS that light an item, so a job
// opened from the projects list keeps "Projects" lit instead of lighting
// nothing.
//
// The old "Invoices" group held two children, "list" and "create". It is one
// destination now; creating an invoice is that page's one primary action and
// the dashboard's, so nothing is further away than it was.
// ---------------------------------------------------------------------------

/** The account types that sign in to /vendors (the session's `type`). */
export const VENDOR_TYPES = ['Linguist', 'Technician', 'Formatter', 'Marketeer'];

export const isVendorType = (type) => VENDOR_TYPES.includes(type);

const P = PATH_LINGUIST;

// Pages a Linguist reaches from a project (a job, an offer, a booking): they
// belong to Projects, so Projects stays lit on them.
const PROJECT_PATHS = [
  P.user.projects,
  '/vendors/translationProjects',
  '/vendors/interpretationProjects',
  '/vendors/mediationProjects',
  '/vendors/translationAllocationResponse',
  '/vendors/barzanoAllocationResponse',
  '/vendors/interpretationAllocationResponse',
  '/vendors/pre-booking',
  '/vendors/booking'
];

const NAV = {
  Linguist: [
    {
      group: 'Work',
      items: [
        {
          key: 'dashboard',
          label: 'Dashboard',
          icon: 'Element3',
          path: P.user.dashboard
        },
        {
          key: 'projects',
          label: 'Projects',
          icon: 'Briefcase',
          path: P.user.projects,
          match: PROJECT_PATHS
        }
      ]
    },
    {
      group: 'Money',
      items: [
        {
          key: 'invoices',
          label: 'Invoices',
          icon: 'ReceiptText',
          path: P.user.invoice,
          // `/vendors/invoices` is the older invoice set (technician pages
          // under the same guard): still an invoice, still this item
          match: ['/vendors/invoice', '/vendors/invoices']
        },
        {
          key: 'payment',
          label: 'Payment details',
          short: 'Payment',
          icon: 'Card',
          path: P.user.paymentInfo
        }
      ]
    },
    {
      group: 'You',
      items: [
        {
          key: 'profile',
          label: 'Profile',
          icon: 'UserEdit',
          path: P.user.profile,
          match: [P.user.profile, '/vendors/change-password']
        },
        {
          key: 'services',
          label: 'Services',
          icon: 'LanguageSquare',
          path: P.user.services
        }
      ]
    }
  ],
  Technician: [
    {
      group: null,
      items: [
        {
          key: 'sheets',
          label: 'Technician sheets',
          short: 'Sheets',
          icon: 'ClipboardText',
          path: P.technician.technicianSheetsList,
          match: ['/vendors/technicianSheets']
        },
        {
          key: 'inventory',
          label: 'Inventory',
          icon: 'Box1',
          path: P.technician.inventory,
          match: ['/vendors/inventory']
        }
      ]
    }
  ],
  Formatter: [
    {
      group: null,
      items: [
        {
          key: 'formatting',
          label: 'Formatting projects',
          short: 'Projects',
          icon: 'RulerPen',
          path: P.formatter.formattingFilesList,
          match: [P.formatter.formattingFilesList, '/vendors/formattingProject']
        },
        {
          key: 'calendar',
          label: 'Projects calendar',
          short: 'Calendar',
          icon: 'Calendar',
          path: P.formatter.projectsCalender
        }
      ]
    }
  ],
  Marketeer: [
    {
      group: null,
      items: [
        {
          key: 'marketing',
          label: 'Marketing channels',
          short: 'Marketing',
          icon: 'StatusUp',
          path: P.marketeer.marketingChannelList
        }
      ]
    }
  ]
};

/** The groups of destinations a vendor type sees. */
export const navGroupsFor = (type) => NAV[type] || [];

/** Every destination of a type, flat, in rail order. */
export const navItemsFor = (type) =>
  navGroupsFor(type).flatMap((group) => group.items);

/**
 * Whether a destination is the page on screen: its own path or one of the
 * paths it owns, matched on segment boundaries (`/vendors/invoice` lights
 * `/vendors/invoice/new`, never `/vendors/invoiceSomething`).
 */
export function isActive(item, pathname) {
  const paths = item.match || [item.path];
  return paths.some(
    (path) =>
      pathname === path ||
      pathname.startsWith(`${path}/`) ||
      pathname.startsWith(`${path}?`)
  );
}

// The phone bar (DESIGN.md §12): five slots, never six. The first four are
// the type's busiest destinations; everything else — and the account — lives
// behind More. A type with fewer destinations shows what it has plus More.
const BAR = {
  Linguist: ['dashboard', 'projects', 'invoices', 'profile']
};

/** The destinations of the phone bar, and the rest that More holds. */
export function barFor(type) {
  const items = navItemsFor(type);
  const keys = BAR[type] || items.slice(0, 4).map((item) => item.key);
  return {
    slots: keys
      .map((key) => items.find((item) => item.key === key))
      .filter(Boolean),
    more: items.filter((item) => !keys.includes(item.key))
  };
}

/** Where the mark at the top of the portal goes: this type's own home. */
export const homeFor = (type) =>
  type === 'Linguist' ? P.user.dashboard : homePathFor(type);

/**
 * The account's own destinations, in the account menu. Only a Linguist's
 * branch has a profile and a password page; the other types have neither, so
 * their menu holds the theme and signing out.
 */
export function accountLinksFor(type) {
  if (type !== 'Linguist') return [];
  return [
    { key: 'profile', label: 'My profile', icon: 'UserEdit', path: P.user.profile },
    {
      key: 'password',
      label: 'Change password',
      icon: 'KeySquare',
      path: '/vendors/change-password'
    }
  ];
}

/** What sits under the name in the account card. */
export const ROLE_WORD = {
  Linguist: 'Linguist',
  Technician: 'Technician',
  Formatter: 'Formatter',
  Marketeer: 'Marketing'
};
